import { ReactNode, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Search, Table2, Upload } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Checkbox } from "../../components/Checkbox/Checkbox";
import { COUNTRY_OPTIONS } from "../../components/CountryCell/countryOptions";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Input } from "../../components/Input/Input";
import { NumberInputWithUnit } from "../../components/NumberInputWithUnit/NumberInputWithUnit";
import { Popover } from "../../components/Popover/Popover";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { Select } from "../../components/Select/Select";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useToast } from "../../components/Toast/toastContext";
import { saveBlob } from "../../lib/download";
import { FieldRow } from "../projects/config/FieldRow";
import { SectionCard } from "../projects/config/SectionCard";
import { StickySaveBar } from "../projects/config/StickySaveBar";
import type {
  BotsAccountAgeConfig,
  BotSource,
  BotsConfig,
  BotsNamesConfig,
  BotsProfileConfig,
} from "./config/types";
import { countLeafChanges, isEqual } from "../projects/config/utils";
import { hasBotsErrors, validateBots } from "./settings/botsValidation";
import { BotProfileEditor } from "./settings/BotProfileEditor";
import { CustomNamesEditor } from "./settings/CustomNamesEditor";
import {
  createRosterRow,
  deleteRosterRow,
  exportRosterCSV,
  listRoster,
  rosterError,
  updateRosterRow,
} from "./api";
import { EditRosterRowModal } from "./EditRosterRowModal";
import { ImportRosterModal } from "./ImportRosterModal";
import { ConfigProblems, ProjectConfigDialogs } from "./ProjectConfigDialogs";
import { RosterTable } from "./RosterTable";
import {
  cardKeysOf,
  columnID,
  copyName,
  formatCount,
  MAX_AGE_DAYS,
  PAGE_SIZE,
  plural,
  rosterCsvHeader,
  SOURCE_LABEL,
  type CardKey,
  type EditState,
} from "./roster";
import { SwitchSourceModal } from "./SwitchSourceModal";
import type { RosterInput, RosterRow, RosterSummary } from "./types";
import { useDebounced } from "./useDebounced";
import type { BotSettingsDraft } from "./useBotSettingsDraft";
import styles from "./SourceTab.module.css";

/**
 * `?tab=source` — where a bot comes from.
 *
 * One switch and two answers. **Generated** is what the platform has always
 * done: a name out of a pool and a card rolled off `bots.profile`. **From
 * table** spends one operator-typed row per bot, in the operator's own order.
 * There is no third state and no «half from the table»: this is the answer to
 * «where does the NEXT bot come from», and that question has one.
 *
 * Saving is split, and the split is on screen because it is invisible
 * otherwise. The mode switch and every write to the table land IMMEDIATELY,
 * like the page header's «Simulation on» — one decision, nothing to stage. The
 * save bar belongs to the DRAFT, and two cards write to it: the Account age
 * range, which stands in both modes, and the Generated form's own names and
 * card rules. Nothing else on this tab stages anything.
 *
 * That bar therefore follows the DRAFT and not the mode, which is what lets a
 * range typed with the table on screen be saved at all — gating it on the mode
 * hid it while the browser went on refusing to navigate away, and left the
 * operator an «unsaved changes» prompt with no Discard button anywhere on the
 * page. What cannot be saved is named beside the range for the same reason: in
 * either mode it is the same draft being refused.
 *
 * The Generated form edits the same bot settings document the Settings tab
 * does, through the same draft machine — a second writer of one document is a
 * second chance to lose a key.
 *
 * Mockups: t-103/bots-source.html, t-107/bots-source-mode-bar.html.
 */

/**
 * How many card columns the table opens with. A project may declare eight
 * keys, and eight columns of numbers with a name column stuck to the left is
 * a table nobody reads — the picker is how the other four are reached, and
 * Compact is how all eight are.
 */
const DEFAULT_COLUMNS = 4;

export function SourceTab({
  projectID,
  draft,
}: {
  projectID: string;
  /**
   * The Bots page's ONE draft of the bot settings, owned by the page rather
   * than by this tab. Settings and Source are two forms over one document, and
   * a draft that lived in the tab was thrown away — twenty fields of edits,
   * with no question asked — the moment somebody clicked another tab.
   */
  draft: BotSettingsDraft;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  const { serverSettings, projectConfig } = draft;

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [offset, setOffset] = useState(0);
  // The box answers every keystroke; the server answers the word. Same idiom,
  // same 250 ms, as the create-guilds preview two files over.
  const settledQ = useDebounced(q);

  // Any narrowing of the table starts it again from the top: page 3 of the
  // whole roster is not page 3 of the eleven rows matching «mash», and an
  // offset carried across a filter answers with an empty page.
  const narrow = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setOffset(0);
  };

  // Loaded in BOTH modes. The summary is what the switch to the table needs
  // before it can ask its question — how many rows are in it and how many
  // guilds would stand still — and a dialog opened without those numbers could
  // only guess at them.
  const roster = useQuery({
    queryKey: [
      "project",
      projectID,
      "bots",
      "roster",
      settledQ,
      status,
      country,
      offset,
    ],
    queryFn: () =>
      listRoster(projectID, {
        q: settledQ,
        status,
        country,
        limit: PAGE_SIZE,
        offset,
      }),
    enabled: !!projectID,
    // The previous page stays on screen while the next one is fetched: a table
    // that emptied into a skeleton between two letters would be a screen
    // blinking at the operator rather than answering them.
    placeholderData: (prev) => prev,
  });
  const summary = roster.data?.summary;

  const source = serverSettings.source;
  const bots = draft.workingSettings;

  const [switchOpen, setSwitchOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<EditState<
    RosterRow,
    RosterInput
  > | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [compact, setCompact] = useState(false);
  const [chosenColumns, setChosenColumns] = useState<string[] | null>(null);

  const cardKeys = useMemo(
    () =>
      cardKeysOf(
        projectConfig.profile.stats_schema,
        projectConfig.profile.meta_schema,
      ),
    [projectConfig.profile],
  );
  const visibleColumns =
    chosenColumns ?? cardKeys.slice(0, DEFAULT_COLUMNS).map(columnID);

  const setNames = (patch: Partial<BotsNamesConfig>) =>
    draft.edit((prev: BotsConfig) => ({
      ...prev,
      names: { ...prev.names, ...patch },
    }));
  const setProfileRules = (profile: BotsProfileConfig) =>
    draft.edit((prev: BotsConfig) => ({ ...prev, profile }));
  // The SAME draft the Generated form writes to, from a card that is on screen
  // in both modes. That is what makes the sticky save bar — `visible` on the
  // draft rather than on the mode — able to save a range typed while the table
  // is the thing being looked at.
  const setAccountAge = (patch: Partial<BotsAccountAgeConfig>) =>
    draft.edit((prev: BotsConfig) => ({
      ...prev,
      account_age: { ...prev.account_age, ...patch },
    }));

  // Three cards write to this draft, so all three are counted: a bar that knew
  // about two of them said «1 unsaved change» over two, and «Discard» would
  // then have thrown away an edit it never admitted to holding.
  const { changes, sections } = useMemo(() => {
    const n = countLeafChanges(serverSettings.names, bots.names);
    const p = countLeafChanges(serverSettings.profile, bots.profile);
    const a = countLeafChanges(
      serverSettings.account_age,
      bots.account_age,
    );
    return {
      changes: n + p + a,
      sections: (n > 0 ? 1 : 0) + (p > 0 ? 1 : 0) + (a > 0 ? 1 : 0),
    };
  }, [serverSettings, bots.names, bots.profile, bots.account_age]);

  // The card rules are read against the project's own schemas: a cosmetic that
  // fell out of a narrowed allow-list is a rule the backend will refuse.
  const botsErrors = useMemo(
    () =>
      validateBots(bots, {
        stats: projectConfig.profile.stats_schema,
        meta: projectConfig.profile.meta_schema,
      }),
    [bots, projectConfig.profile],
  );

  const invalidateRoster = () =>
    qc.invalidateQueries({ queryKey: ["project", projectID, "bots"] });

  // One word on the wire: `{"source": …}` as a merge patch, which leaves every
  // other key of the document exactly as it found it. It used to take an
  // endpoint of its own to say that much.
  const switchSource = useMutation({
    mutationFn: (next: BotSource) => draft.write({ source: next }),
    onSuccess: async (_res, next) => {
      setSwitchOpen(false);
      push({
        variant: "success",
        message:
          next === "table"
            ? "New bots now come from the table."
            : "New bots are generated again.",
      });
    },
    onError: (err) => push({ variant: "danger", message: rosterError(err) }),
  });

  const saveRow = useMutation({
    mutationFn: ({ row, input }: { row: RosterRow | null; input: RosterInput }) =>
      row ? updateRosterRow(projectID, row.id, input) : createRosterRow(projectID, input),
    onSuccess: async (_res, { row }) => {
      setEditing(null);
      setRowError(null);
      await invalidateRoster();
      push({ variant: "success", message: row ? "Row saved." : "Row added." });
    },
    onError: (err) => setRowError(rosterError(err)),
  });

  const removeRow = useMutation({
    mutationFn: (row: RosterRow) => deleteRosterRow(projectID, row.id),
    onSuccess: async () => {
      await invalidateRoster();
      push({ variant: "success", message: "Row deleted." });
    },
    onError: (err) => push({ variant: "danger", message: rosterError(err) }),
  });

  const [exporting, setExporting] = useState(false);
  async function onExport() {
    setExporting(true);
    try {
      await exportRosterCSV(projectID);
    } catch (err) {
      push({ variant: "danger", message: rosterError(err) });
    } finally {
      setExporting(false);
    }
  }

  function onModeChange(next: BotSource) {
    if (next === source || switchSource.isPending) return;
    // Only one direction asks. Switching TO an empty table stops bot creation
    // altogether; switching back breaks nothing and keeps every row.
    if (next === "table" && (summary?.total ?? 0) === 0) {
      setSwitchOpen(true);
      return;
    }
    switchSource.mutate(next);
  }

  async function doSave() {
    if (hasBotsErrors(botsErrors)) return;
    if (await draft.save()) {
      push({ variant: "success", message: "Config saved." });
    }
  }

  // See SettingsTab: a failed read is not a slow one, and this tab draws the
  // name pools and the card rules out of the same document.
  if (draft.loadError) {
    return (
      <Alert variant="danger" title="Couldn't read the bot settings">
        {draft.loadError}
      </Alert>
    );
  }
  if (draft.loading) return <div>Loading…</div>;

  const modeBar = (
    <div className={styles.modeBar}>
      <div>
        <div className={styles.modeTitle}>Where a new bot comes from</div>
        {/* One sentence for both modes. The fear this answers is not about the
            bots — it is about what is already typed in, on BOTH sides — and
            «saved immediately» without an Undo beside it reads as «there is no
            way back». Branching the wording by mode for the sake of one word
            would be two sentences to keep true instead of one. */}
        <div className={styles.modeHint}>
          Applies to bots created from now on; bots already in the pool are
          never touched. Switching erases neither side — the table keeps its
          rows, the Generated form keeps its names and rules — and it can be
          switched back at any time. <b>Saved immediately</b>, like the
          simulation switch above.
        </div>
      </div>
      {/* Settled, not «loaded»: a roster GET that failed must not leave the
          one switch on this tab permanently unusable. The dialog below is what
          covers the missing numbers. */}
      {!roster.isLoading ? (
        <SegmentedToggle
          aria-label="Where a new bot comes from"
          value={source}
          onChange={onModeChange}
          options={[
            {
              value: "generated",
              label: SOURCE_LABEL.generated,
              count: generatedCount(bots),
            },
            {
              value: "table",
              label: SOURCE_LABEL.table,
              count: tableCount(summary),
            },
          ]}
        />
      ) : (
        /* The size of the switch it stands in for, which is the whole job of a
           placeholder. 190×32 was that size while the halves were two bare
           words; with the counts on them the mockup's own bar measures 440×36
           answered, 399 on a project that has set neither pool nor rules and
           311 when the table could not be read. 420×36 is within 5% of the two
           states that are not an error, where 190 held 43% of the width and
           left the bar to jump — and to wrap or not wrap on a narrow window —
           every time this tab loaded. */
        <Skeleton width={420} height={36} />
      )}
    </div>
  );

  /*
   * On screen in BOTH modes, directly under the switch, because the range is
   * the answer to the same question the switch asks — «what does the next bot
   * look like» — and in table mode it is what a row that leaves the cell empty
   * falls back to. Hidden there it would have been an invisible default: the
   * one thing a config screen must never be.
   *
   * What CHANGES with the mode is the copy under the boxes, which is the whole
   * reason the card can be one card. In Generated the range is the answer; in
   * table mode it is the fallback for the rows that give none.
   */
  const accountAgeCard = (
    <div className={styles.accountAge}>
      <SectionCard
        id="account-age"
        title="Account age"
        apiName="bots.account_age"
        dirty={!isEqual(serverSettings.account_age, bots.account_age)}
      >
        <FieldRow
          name="How old the account is at creation"
          apiName="min_days · max_days"
          hint="Drawn per bot, so a pool created in one sweep does not share one birthday."
        >
          <span className={styles.pair}>
            <span className={styles.pairInput}>
              <Input
                type="number"
                min={0}
                max={MAX_AGE_DAYS}
                aria-label="Account age, from"
                value={bots.account_age.min_days}
                onChange={(e) =>
                  setAccountAge({ min_days: dayCount(e.target.value) })
                }
              />
            </span>
            <span className={styles.dash}>–</span>
            <span className={styles.pairInput}>
              <Input
                type="number"
                min={0}
                max={MAX_AGE_DAYS}
                aria-label="Account age, to"
                value={bots.account_age.max_days}
                onChange={(e) =>
                  setAccountAge({ max_days: dayCount(e.target.value) })
                }
              />
            </span>
            <span className={styles.unit}>days old</span>
          </span>
          {source === "generated" ? (
            <>
              <div className={styles.helper}>
                <b>0 – 0</b> is what the platform did before this field existed:
                the account is registered the moment the bot is created. Up to{" "}
                {MAX_AGE_DAYS} days, the ceiling «Create guilds» already uses
                for a guild's age.
              </div>
              {/* The one rule that is not this card's to enforce, said where
                  the number is typed: a guild dated three years back cannot be
                  founded by an account that is three days old, so the account
                  gives way and not the guild. */}
              <div className={styles.helper}>
                A bot that founds or joins a back-dated guild is aged further
                back if its guild needs it: an account is never younger than its
                own first trace.
              </div>
            </>
          ) : (
            <div className={styles.helper}>
              Used for the rows below that leave <b>Account age</b> empty. A row
              with a number of its own is that number, exactly as with Country
              and Behaviour.
            </div>
          )}
        </FieldRow>
      </SectionCard>
    </div>
  );

  /*
   * Why a save was refused, and what the browser already knows would be
   * refused — beside the account-age card rather than inside the Generated
   * column, because that card writes to the same draft from EITHER mode. Left
   * in the column, a range typed over the table greyed the save bar out and
   * said nothing anywhere on the screen about why, and a failed PATCH was
   * drawn by nothing at all.
   */
  const problems = (draft.saveError || botsErrors.problems.length > 0) && (
    <div className={styles.problems}>
      {draft.saveError && (
        <ConfigProblems
          title="Couldn't save"
          intro={draft.saveError}
          problems={draft.saveProblems}
        />
      )}
      {botsErrors.problems.length > 0 && (
        <ConfigProblems
          title={`${botsErrors.problems.length} ${
            botsErrors.problems.length === 1 ? "field" : "fields"
          } cannot be saved`}
          problems={botsErrors.problems}
        />
      )}
    </div>
  );

  return (
    <>
      {/* One box for everything that scrolls, so the room the fixed save bar
          needs at the bottom is reserved once for both modes — see `.tab`. */}
      <div className={styles.tab}>
        {modeBar}
        {accountAgeCard}
        {problems}

        {source === "generated" ? (
          <div className={styles.generated}>
            {/*
              The panel that used to open this column — the one promising the
              table still had all of its rows — is gone, and this is the one
              place t-103 §5 is departed from. It said the right thing at the
              wrong moment: it appeared AFTER the switch, and the doubt it
              answered — «are my two hundred rows still there?» — is felt BEFORE
              it. Its two numbers now stand on the table half of the switch, and
              its promise is in the hint above; keeping both would be one fact on
              screen twice.
            */}
            <SectionCard
              id="names"
              title="Names"
              apiName="bots.names"
              dirty={!isEqual(serverSettings.names, bots.names)}
            >
              <FieldRow
                name="Name pool"
                apiName="bots.names.custom"
                hint="A locale with no list of its own uses the built-in stems."
                stacked
              >
                <CustomNamesEditor
                  value={bots.names.custom}
                  onChange={(custom) => setNames({ custom })}
                  targetMembers={bots.population.target_members}
                />
              </FieldRow>
              {/* It came out of the same old FieldRow as the name pool above,
                  and it belongs beside it: both answer «what does a handle look
                  like», which is the question this card is. */}
              <FieldRow
                name="Digit suffix"
                apiName="bots.names.digits_suffix_percent"
                hint="Jonas_M → Jonas_M92. A pool with no numbers in it looks like a pool."
              >
                <NumberInputWithUnit
                  unit="% of names"
                  value={bots.names.digits_suffix_percent}
                  // An emptied box hands back NaN so the caller can pick its own
                  // default. This one is zero — the number the old boxes wrote,
                  // and the only one that means «no digits». NaN would reach the
                  // config and come back as «NaN is not a percentage», which is
                  // a sentence about the browser rather than about the field.
                  onChange={(n) =>
                    setNames({
                      digits_suffix_percent: Number.isFinite(n) ? n : 0,
                    })
                  }
                  min={0}
                  max={100}
                  aria-label="Digit suffix"
                />
              </FieldRow>
            </SectionCard>

            <SectionCard
              id="card-rules"
              title="Card rules"
              apiName="bots.profile"
              dirty={!isEqual(serverSettings.profile, bots.profile)}
            >
              <BotProfileEditor
                projectID={projectID}
                value={bots.profile}
                onChange={setProfileRules}
                statsSchema={projectConfig.profile.stats_schema}
                metaSchema={projectConfig.profile.meta_schema}
                allowedAvatars={projectConfig.profile.allowed_avatars}
                errors={botsErrors}
                growth={bots.growth}
              />
            </SectionCard>

            <p className={styles.afterNote}>
              The save bar holds this form and the Account age above it, and
              nothing else. The switch and the table are written the moment they
              change — there is nothing to stage there, and a bar announcing
              unsaved changes over a table that is already written would be a
              lie about the table.
            </p>
          </div>
        ) : (
          <TableMode
            projectID={projectID}
            summary={summary}
            rows={roster.data?.rows ?? []}
            total={roster.data?.total ?? 0}
            loading={roster.isLoading}
            // A GET that failed knows nothing about the table, and the screen
            // must not answer for it: undefined data used to read as «0 rows»
            // and draw the empty state, which told an operator with two hundred
            // rows that the table was empty and offered to import the list
            // again — in append mode, on top of the rows already there.
            failed={roster.isError}
            onRetry={() => void roster.refetch()}
            offset={offset}
            onPage={setOffset}
            filtered={settledQ !== "" || status !== "" || country !== ""}
            q={q}
            onQ={narrow(setQ)}
            status={status}
            onStatus={narrow(setStatus)}
            country={country}
            onCountry={narrow(setCountry)}
            cardKeys={cardKeys}
            allowedAvatars={projectConfig.profile.allowed_avatars}
            visibleColumns={visibleColumns}
            onColumns={setChosenColumns}
            compact={compact}
            onCompact={setCompact}
            exporting={exporting}
            onExport={onExport}
            onImport={() => setImportOpen(true)}
            onEdit={(row) => {
              setRowError(null);
              setEditing({ row, input: inputOf(row), title: "Edit row" });
            }}
            onDuplicate={(row) => {
              setRowError(null);
              setEditing({
                row: null,
                input: { ...inputOf(row), display_name: copyName(row.display_name) },
                title: "Duplicate row",
              });
            }}
            onAdd={() => {
              setRowError(null);
              setEditing({ row: null, input: emptyInput(), title: "Add a row" });
            }}
            onDelete={(row) => removeRow.mutate(row)}
          />
        )}
      </div>

      {/*
        Visible on the draft, not on the mode. TWO cards write to that draft —
        the account-age range, from either mode, and the Generated form's own
        two sections — so a bar standing here while the table is on screen is
        holding a real unsaved edit whichever of them made it, and it is the
        only way to Save or Discard what is in it. See the note at the top of
        the file.
      */}
      <StickySaveBar
        visible={draft.dirty}
        changes={changes}
        sections={sections}
        disabled={!draft.dirty || hasBotsErrors(botsErrors)}
        // The short version of the alert above, in place of the change count:
        // the alert names the fields, and this says why the button next to it
        // is grey. A greyed-out button on its own does not say it — and in
        // table mode the alert can be a whole screen further up.
        message={
          botsErrors.problems.length > 0
            ? `${botsErrors.problems.length} ${
                botsErrors.problems.length === 1 ? "field" : "fields"
              } cannot be saved`
            : undefined
        }
        saving={draft.saving}
        onDiscard={draft.askDiscard}
        onSave={() => void doSave()}
      />

      {switchOpen && (
        <SwitchSourceModal
          guildsWaiting={summary ? summary.guilds_waiting : null}
          saving={switchSource.isPending}
          onCancel={() => setSwitchOpen(false)}
          onConfirm={() => switchSource.mutate("table")}
        />
      )}

      {importOpen && (
        <ImportRosterModal
          projectID={projectID}
          cardKeys={cardKeys}
          onClose={() => setImportOpen(false)}
          onDone={(res) => {
            setImportOpen(false);
            void invalidateRoster();
            push({
              variant: "success",
              title: "Import finished",
              message:
                res.removed > 0
                  ? `${plural(res.added, "row", "rows")} written · ${plural(res.removed, "unused row", "unused rows")} replaced · ${formatCount(res.refused)} refused.`
                  : `${plural(res.added, "row", "rows")} written · ${formatCount(res.refused)} refused.`,
            });
          }}
        />
      )}

      {editing && (
        <EditRosterRowModal
          row={editing.row}
          initial={editing.input}
          title={editing.title}
          cardKeys={cardKeys}
          allowedAvatars={projectConfig.profile.allowed_avatars}
          profiles={bots.profiles}
          // The DRAFT's range, not the server's: the boxes that hold it are on
          // the same screen, and a dialog quoting a number the operator has
          // just changed above it would be arguing with them.
          accountAge={bots.account_age}
          takenNames={(roster.data?.rows ?? [])
            .filter((r) => r.id !== editing.row?.id)
            .map((r) => r.display_name)}
          saving={saveRow.isPending}
          error={rowError}
          onClose={() => {
            setEditing(null);
            setRowError(null);
          }}
          onSave={(input) => saveRow.mutate({ row: editing.row, input })}
        />
      )}

      <ProjectConfigDialogs
        draft={draft}
        changes={changes}
        sections={sections}
      />
    </>
  );
}

/**
 * What stands behind the Generated half, in the words of the form below it.
 *
 * Read from the DRAFT and not from the saved config: the form is right there
 * on the same screen, and a half arguing with the field under it would be
 * worse than a half a save behind the server. Which of these numbers is not
 * written yet is the save bar's sentence to say, not this one's.
 *
 * A number that does not exist is not invented: no custom list anywhere is not
 * «0 names», it is the built-in stems doing the naming, and no rules at all is
 * the platform's behaviour before `bots.profile` existed. Both are working
 * states, and both are said as such.
 */
function generatedCount(bots: BotsConfig): string {
  const names = Object.values(bots.names.custom).reduce(
    (n, list) => n + list.length,
    0,
  );
  // All THREE collections of `bots.profile`, avatar included. It is a rule the
  // draw applies like any other, and the editor right under this switch draws
  // it — so a project whose only rule is an avatar would otherwise read «no
  // rules» over a form showing one.
  const rules =
    (bots.profile.avatar?.length ?? 0) +
    bots.profile.stats.length +
    bots.profile.meta.length;
  return [
    names > 0 ? plural(names, "name", "names") : "built-in names",
    rules > 0 ? plural(rules, "rule", "rules") : "no rules",
  ].join(" · ");
}

/**
 * What stands behind the From table half — from the SERVER, because the table
 * is written the moment it changes and has no draft to be ahead of.
 *
 * `undefined` when the roster GET has not answered: the half then says NOTHING
 * rather than «0 rows». A count invented about somebody else's table is the
 * very mistake that once had the empty state offer to import a list on top of
 * two hundred rows.
 */
function tableCount(s: RosterSummary | undefined): string | undefined {
  if (!s) return undefined;
  // An empty table is an answer, and «0 rows · 0 left» is that answer said
  // twice with a number that measures nothing.
  if (s.total === 0) return "empty";
  return [
    plural(s.total, "row", "rows"),
    s.left === 0 ? "none left" : `${formatCount(s.left)} left`,
  ].join(" · ");
}

/**
 * The number in one of the two age boxes.
 *
 * An emptied box hands back `""`, and a box the browser could not read as a
 * number hands back `""` as well; `Number` turns the first into 0 and anything
 * genuinely unreadable into NaN. NaN must not reach the config — it is saved
 * as `null` and comes back as a sentence about the browser rather than about
 * the field — and zero is this field's own answer to an empty box, exactly as
 * it is the Digit suffix's above: it is the default, and it is how «registered
 * the moment the bot is created» is spelled.
 */
function dayCount(raw: string): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function inputOf(row: RosterRow): RosterInput {
  return {
    display_name: row.display_name,
    avatar: row.avatar,
    country: row.country,
    profile_key: row.profile_key,
    age_days: row.age_days,
    stats: row.stats,
    meta: row.meta,
  };
}

function emptyInput(): RosterInput {
  return {
    display_name: "",
    // Null and not "": a new row names no avatar, so the bot it becomes is
    // created without one. Nothing is drawn in its place — unlike the country,
    // the behaviour and the age below.
    avatar: null,
    country: null,
    profile_key: null,
    // Null and not 0: a new row draws its age from the range, the same way it
    // draws its country and its behaviour.
    age_days: null,
    stats: {},
    meta: {},
  };
}

/* --- From table ----------------------------------------------------------- */

interface TableModeProps {
  projectID: string;
  summary: RosterSummary | undefined;
  rows: RosterRow[];
  total: number;
  loading: boolean;
  /** The roster GET was refused or never answered. Nothing below it is known. */
  failed: boolean;
  onRetry: () => void;
  /** How many rows this page skipped, and how to move. */
  offset: number;
  onPage: (offset: number) => void;
  filtered: boolean;
  q: string;
  onQ: (v: string) => void;
  status: string;
  onStatus: (v: string) => void;
  country: string;
  onCountry: (v: string) => void;
  cardKeys: CardKey[];
  /** `profile.allowed_avatars`, passed straight through to the table: an
   *  avatar outside a non-empty whitelist is marked there. */
  allowedAvatars: string[];
  visibleColumns: string[];
  onColumns: (next: string[]) => void;
  compact: boolean;
  onCompact: (next: boolean) => void;
  exporting: boolean;
  onExport: () => void;
  onImport: () => void;
  onEdit: (row: RosterRow) => void;
  onDuplicate: (row: RosterRow) => void;
  onAdd: () => void;
  onDelete: (row: RosterRow) => void;
}

function TableMode(p: TableModeProps) {
  const s = p.summary;
  const empty = !!s && s.total === 0;
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      {/*
        A failed request says NOTHING about the table, and this is where the
        screen says so. Every count below — the runway, the two alerts, the
        empty state — is drawn from an answer; without one they are all silent,
        and silence read as «the table is empty, import it again».
      */}
      {p.failed && (
        <div className={styles.alertStack}>
          <Alert
            variant="danger"
            title="Couldn't read the table"
            action={
              <Button variant="secondary" size="sm" onClick={p.onRetry}>
                Try again
              </Button>
            }
          >
            The rows are where they were — nothing on this page has touched
            them. Until this loads there is no telling how many there are or how
            many are left, so nothing below is up to date.
          </Alert>
        </div>
      )}
      {s && !empty && <Runway projectID={p.projectID} s={s} />}

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Input
            value={p.q}
            onChange={(e) => p.onQ(e.target.value)}
            placeholder="Search by name…"
            aria-label="Search rows by name"
            leadingIcon={<Search size={14} aria-hidden="true" />}
          />
        </div>
        {/* The prefix stays on the CHOSEN option too. Without it the closed
            box read «used», which is a row's status and not the name of the
            filter that is narrowing the table. */}
        <Select
          aria-label="Status"
          value={p.status}
          onChange={p.onStatus}
          options={[
            { value: "", label: "Status: all" },
            { value: "unused", label: "Status: unused" },
            { value: "used", label: "Status: used" },
            { value: "blocked", label: "Status: name taken" },
          ]}
        />
        {/*
          Every country, not the ones this page happens to be showing. The
          filter narrows SERVER-side by exact code over the whole table, so a
          list built from the fifty rows on screen could not reach a country
          used only by row 120 — and «the way through two hundred rows is the
          search box and the two filters» would not be true.
        */}
        <Select
          aria-label="Country"
          value={p.country}
          onChange={p.onCountry}
          options={[
            { value: "", label: "Country: all", separatorAfter: true },
            ...COUNTRY_OPTIONS,
          ]}
        />
        <button
          ref={pickerRef}
          type="button"
          className={styles.pickerTrigger}
          aria-expanded={pickerOpen}
          aria-haspopup="dialog"
          onClick={() => setPickerOpen((o) => !o)}
        >
          Columns: {p.visibleColumns.length} of {p.cardKeys.length}
          <span aria-hidden="true">▾</span>
        </button>
        <Popover
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          anchorRef={pickerRef}
          placement="bottom-start"
        >
          <div className={styles.pickerPanel}>
            <div className={styles.pickerHead}>Card columns</div>
            {p.cardKeys.map((k) => {
              const id = columnID(k);
              const on = p.visibleColumns.includes(id);
              return (
                <div key={id} className={styles.pickerRow}>
                  <Checkbox
                    checked={on}
                    label={id}
                    onChange={(next) =>
                      p.onColumns(
                        next
                          ? [...p.visibleColumns, id]
                          : p.visibleColumns.filter((c) => c !== id),
                      )
                    }
                  />
                </div>
              );
            })}
            {p.cardKeys.length === 0 && (
              <div className={styles.pickerRow}>
                This project declares no card keys.
              </div>
            )}
          </div>
        </Popover>
        <SegmentedToggle
          aria-label="Table density"
          value={p.compact ? "compact" : "columns"}
          onChange={(v) => p.onCompact(v === "compact")}
          options={[
            { value: "columns", label: "Columns" },
            { value: "compact", label: "Compact" },
          ]}
        />
        <span className={styles.spacer} />
        <Button
          variant="secondary"
          icon={<Download size={14} />}
          loading={p.exporting}
          onClick={p.onExport}
        >
          Export CSV
        </Button>
        <Button icon={<Upload size={14} />} onClick={p.onImport}>
          Import CSV
        </Button>
      </div>

      <RosterTable
        rows={p.rows}
        total={p.total}
        loading={p.loading}
        compact={p.compact}
        cardKeys={p.cardKeys}
        allowedAvatars={p.allowedAvatars}
        visible={p.visibleColumns}
        offset={p.offset}
        onPage={p.onPage}
        projectID={p.projectID}
        onEdit={p.onEdit}
        onDuplicate={p.onDuplicate}
        onDelete={p.onDelete}
        empty={
          // «Empty» is an ANSWER — a successful response with no rows in it.
          // A failed one has no answer to give, and the alert above is the
          // whole of what this screen knows.
          p.failed ? (
            <EmptyState
              icon={<Table2 size={20} />}
              title="The table could not be read"
              description="This is the request, not the table. Nothing has been deleted and nothing has been imported."
            />
          ) : p.filtered ? (
            <EmptyState
              title="No row matches"
              description="Clear the search or the filters to see the rest of the table."
            />
          ) : (
            <EmptyState
              icon={<Table2 size={20} />}
              title="The table is empty — no bot will be created"
              description="A row is one future bot: a name, a country, a behaviour and the exact card it carries. Import the list you already have, or start the file from this project's own header."
              action={
                <div className={styles.emptyActions}>
                  <Button icon={<Upload size={14} />} onClick={p.onImport}>
                    Import CSV
                  </Button>
                  <Button
                    variant="secondary"
                    icon={<Download size={14} />}
                    onClick={() =>
                      saveBlob(
                        new Blob([`${rosterCsvHeader(p.cardKeys)}\r\n`], {
                          type: "text/csv;charset=utf-8",
                        }),
                        "bot-roster-template.csv",
                      )
                    }
                  >
                    Download the header
                  </Button>
                  <Button variant="ghost" onClick={p.onAdd}>
                    Add a row
                  </Button>
                </div>
              }
            />
          )
        }
      />

      {s && (
        <div className={styles.alertStack}>
          {s.blocked > 0 && (
            <Alert
              variant="warn"
              title={
                s.blocked === 1
                  ? "1 row cannot become a bot: the name is taken"
                  : `${formatCount(s.blocked)} rows cannot become bots: the name is taken`
              }
            >
              Display names are unique across a project — bots and living
              players alike.{" "}
              {/* NAMED, as far as this page has loaded them. «2 rows» is a
                  number an operator has to go and resolve; the names are the
                  rows themselves, and renaming one is the whole of the fix. */}
              {blockedNames(p.rows, s.blocked)} were free when they were
              imported and have since been registered by real players. The
              filling worker skips them and they stay here until they are
              renamed.{" "}
              {/* No ellipsis: this narrows the table, it does not open a
                  dialog, and the three dots promised one. */}
              <button
                type="button"
                className={styles.alertBtn}
                onClick={() => p.onStatus("blocked")}
              >
                Show {s.blocked === 1 ? "that row" : "those rows"}
              </button>
            </Alert>
          )}
          {/*
            Only over a table that HAS rows. `left === 0` on a table that was
            never filled is not «the project has stopped creating bots» — it
            never started — and the sentence stacked under the empty state,
            which had just said the same thing better. `total` is what tells
            the two apart.
          */}
          {s.total > 0 && (
            <Alert
              variant="info"
              title={
                s.left === 0
                  ? "No row is left — the project has stopped creating bots"
                  : `${plural(s.left, "row", "rows")} left — after that the project stops creating bots`
              }
            >
              Under-filled guilds simply stay under-filled, and Overview names
              the reason. That is the whole meaning of the table being the
              source. To keep the pool growing, import more rows or switch back
              to Generated.
            </Alert>
          )}
        </div>
      )}
    </>
  );
}

/**
 * The blocked rows, by name, as far as this page has loaded them.
 *
 * Two names and then a count: nine names in an alert is the table again, and
 * the point of naming any is that the first one can be gone and renamed. A page
 * narrowed by a filter may hold none of them, and then the count is all there
 * is to say — a name invented for a row that is not on screen would be worse
 * than a number.
 */
function blockedNames(rows: RosterRow[], blocked: number): string {
  const names = rows
    .filter((r) => r.status === "blocked")
    .slice(0, 2)
    .map((r) => r.display_name);
  if (names.length === 0) {
    return `${plural(blocked, "row", "rows")} of this table`;
  }
  const rest = blocked - names.length;
  if (rest > 0) {
    return `${names.join(", ")} and ${formatCount(rest)} more`;
  }
  return names.join(" and ");
}

/**
 * How much of the table is left, and — the number that makes it mean anything
 * — how long that is. «34 rows» is a fortnight on one project and an afternoon
 * on another, and the count alone says which only to somebody who already
 * knows.
 */
function Runway({ projectID, s }: { projectID: string; s: RosterSummary }) {
  const pct = (n: number) => `${(n / Math.max(1, s.total)) * 100}%`;
  return (
    <div className={styles.runway}>
      <div className={styles.runwayHead}>
        <div>{runwayLine(s)}</div>
        {s.guilds_waiting > 0 && (
          <Link className={styles.runwayLink} to={`/projects/${projectID}/guilds`}>
            {plural(s.guilds_waiting, "guild is", "guilds are")} waiting for
            members ›
          </Link>
        )}
      </div>
      <div className={styles.runwayBar}>
        <span className={styles.runwayUsed} style={{ width: pct(s.used) }} />
        <span className={styles.runwayBad} style={{ width: pct(s.blocked) }} />
      </div>
      <div className={styles.runwayLegend}>
        <span>
          <i className={`${styles.sw} ${styles.swUsed}`} />
          {formatCount(s.used)} used
        </span>
        <span>
          <i className={`${styles.sw} ${styles.swLeft}`} />
          {formatCount(s.left)} left
        </span>
        <span>
          <i className={`${styles.sw} ${styles.swBad}`} />
          {formatCount(s.blocked)} blocked
        </span>
        <span className={styles.spacer} />
        <span className={styles.mono}>
          {plural(s.total, "row", "rows")} total
        </span>
      </div>
    </div>
  );
}

function runwayLine(s: RosterSummary): ReactNode {
  const left = (
    <>
      <b>{formatCount(s.left)}</b> {s.left === 1 ? "row" : "rows"} left
    </>
  );
  // Null is not «forever». Nothing has been spent this week, so there is no
  // rate to divide by — and the strip says that rather than printing a dash
  // where a number an operator could act on ought to be.
  if (s.days_left === null) {
    return (
      <>
        {left} — no bot has come out of the table in the last week, so there is
        no fill rate to measure them against
      </>
    );
  }
  return (
    <>
      {left} — enough for about <b>{describeDays(s.days_left)}</b> at the
      current fill rate
    </>
  );
}

function describeDays(days: number): string {
  const whole = Math.round(days);
  if (whole <= 0) return "less than a day";
  return `${formatCount(whole)} ${whole === 1 ? "day" : "days"}`;
}
